import React, { useState, useRef, useEffect } from 'react';
import _set from 'lodash/set';
import _unset from 'lodash/unset';
import _clone from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';

import { UiKit } from 'components';
import { ReactTable, Search, SearchState } from 'components/ReactTable';
import { ReactComponent as ProductIcon } from 'assets/ss-icons/zero-state-icons/product.svg';
import { If } from 'utils';

/**
 * Generic ListSelectionModal component for selecting items from a list
 *
 * @param {Object} props
 * @param {Function} props.onComplete - Callback when selection is confirmed
 * @param {Function} props.onCancel - Callback when selection is cancelled
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Array} props.initialSelected - Initially selected items
 * @param {string} props.title - Modal title
 * @param {string} props.apiEndpoint - API endpoint for fetching items
 * @param {Function} props.apiMethod - API method (GET, POST, etc.)
 * @param {Object} props.filters - Filters for the API request
 * @param {string} props.idField - Field to use as unique identifier
 * @param {string} props.searchPlaceholder - Placeholder for search input
 * @param {number} props.searchDelay - Delay for search input
 * @param {Object} props.className - CSS class for the modal
 * @param {React.Component} props.ItemsListComponent - Component to render the list of items
 * @param {React.Component} props.SelectedItemsComponent - Component to render selected items
 */
export default function ListSelectionModal({
  onComplete,
  onCancel,
  isOpen,
  initialSelected = [],
  title = 'Select Items',
  apiEndpoint,
  apiMethod,
  filters = { page_size: 9 },
  idField = 'ObjectId',
  searchPlaceholder = 'Search...',
  searchDelay = 500,
  className,
  ItemsListComponent
}) {
  const [selected, setSelected] = useState({});
  const initialItemsMap = useRef({});
  const filtersRef = useRef(filters).current;
  const canSave = _isEqual(selected, initialItemsMap.current);

  useEffect(() => {
    if (isOpen) {
      const itemsMap = initialSelected.reduce((acc, item) => {
        const key = item[idField];
        acc[key] = {
          id: key,
          ...item
        };
        return acc;
      }, {});
      initialItemsMap.current = itemsMap;
      setSelected(itemsMap);
    } else {
      initialItemsMap.current = {};
      setSelected({});
    }
  }, [isOpen, initialSelected, idField]);

  const toggleSelection = item => {
    const key = item[idField];
    const itemsMap = _clone(selected);
    if (key in itemsMap) {
      _unset(itemsMap, key);
    } else {
      _set(itemsMap, key, {
        id: key,
        ...item
      });
    }
    setSelected(itemsMap);
  };

  const handleComplete = () => {
    const selectedItems = Object.values(selected).map(item => {
      const result = { ...item };
      result[idField] = item.id;
      return result;
    });
    onComplete(selectedItems);
  };

  const clearAll = () => setSelected({});

  return (
    <UiKit.Modal size={'md'} isOpen={isOpen} onClose={onCancel}>
      <UiKit.Modal.Header title={title} />
      <UiKit.Modal.Body className={className}>
        <ReactTable
          apiEndpoint={apiEndpoint}
          method={apiMethod}
          filters={filtersRef}
        >
          <Search
            delay={searchDelay}
            preventDisable
            customClass="mb-3"
            placeholder={searchPlaceholder}
          />
          <SearchState
            noRecordIcon={<ProductIcon />}
            noRecordsMessage="There is no product."
          >
            {ItemsListComponent && (
              <ItemsListComponent
                toggle={toggleSelection}
                selected={selected}
              />
            )}
          </SearchState>
        </ReactTable>
      </UiKit.Modal.Body>
      <UiKit.Modal.Footer
        onConfirm={handleComplete}
        isConfirmDisabled={canSave}
        confirmBtnText={`Confirm (${Object.keys(selected).length})`}
      >
        <If test={!_isEmpty(selected)}>
          <UiKit.Button variant="ghost" onClick={clearAll}>
            Unselect All
          </UiKit.Button>
        </If>
      </UiKit.Modal.Footer>
    </UiKit.Modal>
  );
}
